import { createContext, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { PAGE_SCHEMAS, pageDataPath } from './siteConfig';

/**
 * Server render passes the payload through context; nothing else does.
 */
export const PagePayloadContext = createContext(null);

const EMBED_ID = 're-page-data';
let embeddedCache;
let embeddedPath = null;

const normalisePath = (path) => (path || '/').replace(/\/+$/, '') || '/';

/** The page data the generator wrote into the HTML, parsed once. */
export function readEmbeddedPayload() {
  if (embeddedCache !== undefined) return embeddedCache;
  embeddedCache = null;
  if (typeof document === 'undefined') return embeddedCache;
  // The embedded data describes the URL the HTML was served for, and only
  // that URL; after client-side navigation it no longer applies.
  embeddedPath = normalisePath(window.location.pathname);
  const node = document.getElementById(EMBED_ID);
  if (!node) return embeddedCache;
  try {
    embeddedCache = JSON.parse(node.textContent || 'null');
  } catch (error) {
    embeddedCache = null;
  }
  return embeddedCache;
}

/** Test hook. */
export function resetEmbeddedPayloadCache() {
  embeddedCache = undefined;
  embeddedPath = null;
}

function matches(payload, type, slug) {
  if (!payload) return false;
  if (payload.schema !== PAGE_SCHEMAS[type]) return false;
  return slug == null || payload.slug === slug;
}

class PageNotFound extends Error {}

async function fetchPayload(type, slug) {
  const response = await fetch(pageDataPath(type, slug), { headers: { Accept: 'application/json' } });
  if (response.status === 404) throw new PageNotFound();
  if (!response.ok) throw new Error(`Page data request failed (${response.status})`);
  // An unknown path on the static host can fall through to the SPA shell,
  // which is HTML with status 200. Treat anything that isn't JSON as missing.
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('json')) throw new PageNotFound();
  const data = await response.json();
  if (!matches(data, type, slug)) throw new PageNotFound();
  return data;
}

/**
 * Resolve page data for a static page.
 *
 * 1. Server render: the context value.
 * 2. First client render of a prerendered page: the embedded JSON, read
 *    synchronously so hydration sees exactly what the server rendered.
 * 3. Client-side navigation: the static JSON file for that page.
 *
 * Returns { status: 'ready' | 'loading' | 'not-found' | 'error', payload }.
 */
export function usePagePayload(type, slug) {
  const serverValue = useContext(PagePayloadContext);
  const location = useLocation();
  let embedded = serverValue ? null : readEmbeddedPayload();
  if (embedded && embeddedPath !== normalisePath(location.pathname)) embedded = null;

  let immediate = null;
  let notFound = false;
  const source = serverValue ? serverValue.payload : embedded;
  if (source?.schema === PAGE_SCHEMAS['not-found']) notFound = true;
  else if (matches(source, type, slug)) immediate = source;
  else if (serverValue) notFound = true;

  const query = useQuery({
    queryKey: ['seo-page', type, slug],
    queryFn: () => fetchPayload(type, slug),
    enabled: !immediate && !notFound && typeof window !== 'undefined',
    staleTime: 5 * 60 * 1000,
    retry: (count, error) => !(error instanceof PageNotFound) && count < 2,
  });

  if (immediate) return { status: 'ready', payload: immediate };
  if (notFound) return { status: 'not-found', payload: null };
  if (query.data) return { status: 'ready', payload: query.data };
  if (query.error instanceof PageNotFound) return { status: 'not-found', payload: null };
  if (query.error) return { status: 'error', payload: null, error: query.error };
  return { status: 'loading', payload: null };
}
